import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { ApiError } from '../lib/api';
import { applyLocale, pick } from '../i18n';

export function LoginPage() {
  const { t, i18n } = useTranslation();
  const { login, publicConfig } = useAuth();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [askTotp, setAskTotp] = useState(false);
  const [useRecovery, setUseRecovery] = useState(false);
  const [recovery, setRecovery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const locale = i18n.language;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      // No navigation here: signing in changes the auth state, the router re-runs its guards with it, and the
      // guard of this page sends a signed-in user on (to the page they came for, or to the password change).
      await login(identifier, password, askTotp && !useRecovery ? totp : undefined, askTotp && useRecovery ? recovery : undefined);
    } catch (err) {
      if (err instanceof ApiError && err.status === 423) {
        const until = err.details?.locked_until ? new Date(String(err.details.locked_until)).toLocaleTimeString(locale) : '';
        setError(t('login.locked', { time: until }));
      } else if (err instanceof ApiError && err.code === 'totp_required') setAskTotp(true);
      else if (err instanceof ApiError && err.code === 'totp_invalid') setError(t('login.totpInvalid'));
      else if (err instanceof ApiError && err.status === 401) setError(t('login.invalid'));
      else setError(t('login.failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-100 p-4 dark:bg-neutral-950">
      <form onSubmit={submit} className="card w-full max-w-sm p-6">
        <div className="mb-6 flex flex-col items-center gap-2 text-center">
          {publicConfig?.logo_url ? (
            <div className="h-16 w-16 overflow-hidden">
              <img src={publicConfig.logo_url} alt="" className="max-h-full max-w-full object-contain" />
            </div>
          ) : (
            <div className="flex h-12 w-12 items-center justify-center rounded-xl text-lg text-white" style={{ background: publicConfig?.primary_color ?? '#2E7D32' }}>
              {(publicConfig?.display_name_en ?? 'M').slice(0, 1)}
            </div>
          )}
          <h1 className="text-lg font-semibold">{t('login.title')}</h1>
          {publicConfig && (
            <p className="text-sm text-neutral-500" dir="auto">
              {pick(locale, publicConfig.display_name_en, publicConfig.display_name_ar)}
            </p>
          )}
        </div>
        <label className="label" htmlFor="identifier">
          {t('login.identifier')}
        </label>
        <input id="identifier" className="input mb-3" value={identifier} onChange={(e) => setIdentifier(e.target.value)} autoComplete="username" required autoFocus />
        <label className="label" htmlFor="password">
          {t('login.password')}
        </label>
        <input id="password" type="password" className="input mb-4" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
        {askTotp && !useRecovery && (
          <>
            <label className="label" htmlFor="totp">
              {t('login.totp')}
            </label>
            <input id="totp" className="input mb-1" inputMode="numeric" maxLength={6} dir="ltr" autoComplete="one-time-code" value={totp} onChange={(e) => setTotp(e.target.value.replace(/\D/g, ''))} required autoFocus />
            <p className="mb-1 text-xs text-neutral-500">{t('login.totpHint')}</p>
            <button type="button" className="mb-3 text-xs text-brand-700 hover:underline dark:text-brand-300" onClick={() => { setUseRecovery(true); setError(null); }}>{t('login.useRecovery')}</button>
          </>
        )}
        {askTotp && useRecovery && (
          <>
            <label className="label" htmlFor="recovery">
              {t('login.recovery')}
            </label>
            <input id="recovery" className="input mb-1 font-mono" dir="ltr" autoComplete="off" spellCheck={false} value={recovery} onChange={(e) => setRecovery(e.target.value)} required autoFocus />
            <p className="mb-1 text-xs text-neutral-500">{t('login.recoveryHint')}</p>
            <button type="button" className="mb-3 text-xs text-brand-700 hover:underline dark:text-brand-300" onClick={() => { setUseRecovery(false); setError(null); }}>{t('login.useAuthenticator')}</button>
          </>
        )}
        {error && (
          <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-200" role="alert">
            {error}
          </p>
        )}
        <button type="submit" className="btn-primary w-full" disabled={busy}>
          {busy ? t('login.signingIn') : t('login.signIn')}
        </button>
        {publicConfig?.login.central && publicConfig.login.central_url && (
          <a href={`${publicConfig.login.central_url}/sso/start?entity=${publicConfig.slug}`} className="btn-secondary mt-2 w-full">
            {t('login.centralSso')}
          </a>
        )}
        <button type="button" className="mx-auto mt-4 flex items-center gap-1 text-xs text-neutral-500 hover:underline" onClick={() => applyLocale(locale === 'ar' ? 'en' : 'ar')}>
          <Globe size={12} /> {locale === 'ar' ? 'English' : 'العربية'}
        </button>
      </form>
    </div>
  );
}
